import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  IconButton,
  Paper,
} from '@mui/material';
import { Close } from '@mui/icons-material';

const FeatureSelector = ({
  featureIds = [],
  onChange,
  features = [],
  error,
  helperText,
}) => {
  const [inputValue, setInputValue] = useState('');

  // Filter out features that are already selected
  const availableFeatures = features.filter(
    (feature) => !featureIds.includes(feature.id)
  );

  const handleAddFeature = (newFeature) => {
    if (newFeature && !featureIds.includes(newFeature.id)) {
      onChange([...featureIds, newFeature.id]); // Store only the ID
      setInputValue('');
    }
  };

  const handleRemoveFeature = (featureIdToRemove) => {
    onChange(featureIds.filter((id) => id !== featureIdToRemove));
  };

  return (
    <FormControl fullWidth error={error}>
      {featureIds.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Features:
          </Typography>
          <Paper variant="outlined" sx={{ p: 1 }}>
            {featureIds.map((featureId) => {
              const feature = features.find((f) => f.id === featureId);
              return (
                <Box
                  key={featureId}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    px: 1,
                    '&:not(:last-child)': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <Typography>
                    {feature
                      ? feature.name
                      : `Unknown Feature (ID: ${featureId})`}
                  </Typography>
                  <IconButton
                    edge="end"
                    aria-label="remove"
                    onClick={() => handleRemoveFeature(featureId)}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Paper>
        </Box>
      )}

      <Autocomplete
        options={availableFeatures}
        getOptionLabel={(option) => option.name}
        value={null}
        onChange={(event, newValue) => handleAddFeature(newValue)}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add a feature"
            placeholder="Search features..."
            fullWidth
          />
        )}
        noOptionsText="No features available or already added"
        disabled={availableFeatures.length === 0}
      />

      {error && <FormHelperText>{helperText}</FormHelperText>}
      {availableFeatures.length === 0 && featureIds.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          All available features have been added to this package.
        </Typography>
      )}
    </FormControl>
  );
};

export default FeatureSelector;
